import React, {ReactNode} from 'react';

import {pluralize} from 'BenchlingUtil';
import DataConnector from 'connect/DataConnector';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {Plate} from 'constants/SampleConstants';
import {CICOItemIdsToAssignee, SampleStatusItemForValidation} from 'constants/SampleStatusConstants';
import CheckInModal from 'inventory/check-in-check-out/CheckInModal';
import CheckOutModal from 'inventory/check-in-check-out/CheckOutModal';
import * as CICOActions from 'inventory/check-in-check-out/CICOActions';
import ReserveModal from 'inventory/check-in-check-out/ReserveModal';
import {ManagedModelType} from 'models/ManagedModelDefinitions';
import {PlateSchemaType} from 'samples/storables/StorableSchemaConstants';
import getTestProps from 'test/lib/cypress/getTestProps';
import Tooltip from 'ui-library/bs/Tooltip';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Menu from 'ui-library/menus/Menu';
import Option from 'ui-library/menus/Option';
import runModal from 'ui-library/runModal';
import APIUtil from 'util/APIUtil';

interface Props {
  actionSource: ActionOrigin; // for product metrics - where users are initiating the CICO action from
  buttonStyle: ButtonStyle;
  buttonClassName?: string;
  items: Array<SampleStatusItemForValidation>;
  // This callback aims to track item selections.
  onClickTracking?: (selectedItemName: string) => void;
}

interface EnhancedProps {
  containsFixedPlateContainers: boolean;
}

class CICODropdownConnector extends DataConnector<Props, EnhancedProps> {
  override renderLoading(): ReactNode {
    return null;
  }

  enhanceProps(): EnhancedProps {
    const parentPlatesById = this.getOrFetchMultipleRequired(
      ManagedModelType.STORABLE,
      this.props.items
        .filter((item) => item.parentStorableId && APIUtil.isPlate(item.parentStorableId))
        .map(({parentStorableId}) => parentStorableId!)
    );

    return {
      containsFixedPlateContainers: Object.values(parentPlatesById).some(
        (p) => (p as Plate).storableSchema.plateSchemaType === PlateSchemaType.FIXED_PLATE
      ),
    };
  }
}

/**
 * Button dropdown for selecting which check in/check out action to take.
 */
class CICODropdown extends React.Component<Props & EnhancedProps> {
  _onReserve = (itemIdsToAssignee: CICOItemIdsToAssignee, comment?: string): void => {
    CICOActions.reserve(itemIdsToAssignee, {comment});
  };
  _onCheckOut = (itemIdsToAssignee: CICOItemIdsToAssignee, comment?: string): void => {
    CICOActions.checkOut(itemIdsToAssignee, {comment});
  };
  _onCheckIn = (itemIds: Array<string>, comment?: string): void => {
    CICOActions.checkIn(itemIds, {comment});
  };

  override render(): JSX.Element | null {
    const itemsNotEmpty = this.props.items.length > 0;
    const allItemsAreContainers = this.props.items.every((item) => APIUtil.isContainer(item.id));
    const allItemsAreWritable = this.props.items.every((item) => item.permissions.writable);
    const isValid =
      itemsNotEmpty &&
      allItemsAreContainers &&
      !this.props.containsFixedPlateContainers &&
      allItemsAreWritable;
    let tooltipText = 'Check in / Check out';
    if (!itemsNotEmpty) {
      tooltipText = 'Select items to check in / check out';
    } else if (!allItemsAreContainers) {
      tooltipText = 'Only containers can be checked in / checked out';
    } else if (this.props.containsFixedPlateContainers) {
      tooltipText = 'Containers in well plates cannot be checked in / checked out';
    } else if (!allItemsAreWritable) {
      tooltipText = `You need write permissions for ${pluralize(
        this.props.items.length,
        'this item',
        'these items'
      )} to check in / check out`;
    }
    return (
      <div className="buttonGroup" {...getTestProps({component: 'CICODropdown', element: 'root'})}>
        <Tooltip title={tooltipText}>
          <Dropdown
            menu={
              <Menu height={Menu.HEIGHTS.MEDIUM} width={Menu.WIDTHS.MEDIUM}>
                <Option
                  onSelect={async () => {
                    this.props.onClickTracking?.('Reserve');
                    await runModal(ReserveModal, {
                      onReserve: this._onReserve,
                      items: this.props.items,
                      analyticsMetadata: {actionOrigin: this.props.actionSource},
                    });
                  }}
                >
                  Reserve
                </Option>
                <Option
                  onSelect={async () => {
                    this.props.onClickTracking?.('CheckOut');
                    await runModal(CheckOutModal, {
                      onCheckOut: this._onCheckOut,
                      items: this.props.items,
                      analyticsMetadata: {actionOrigin: this.props.actionSource},
                    });
                  }}
                  dataTestAttrs={{component: 'CICODropdown', element: 'dropdownOption', key: 'checkOut'}}
                >
                  Check out
                </Option>
                <Option
                  onSelect={async () => {
                    this.props.onClickTracking?.('CheckIn');
                    await runModal(CheckInModal, {
                      onCheckIn: this._onCheckIn,
                      items: this.props.items,
                      analyticsMetadata: {actionOrigin: this.props.actionSource},
                    });
                  }}
                >
                  Check in
                </Option>
              </Menu>
            }
          >
            <Button
              aria-label="Check-in / Check-out"
              buttonStyle={this.props.buttonStyle}
              className={this.props.buttonClassName}
              size={ButtonSize.SMALL}
              disabled={!isValid}
              icon="check-in-check-out"
              isDropdown={true}
              dataTestAttrs={{component: 'CICODropdown', element: 'button', key: 'toggleDropdown'}}
            />
          </Dropdown>
        </Tooltip>
      </div>
    );
  }
}

export default CICODropdownConnector.connect(CICODropdown);
